import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast, Toaster } from 'sonner';
import { LogOut, Save, Lock, Loader2 } from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;
const TOKEN_KEY = 'admin_token';

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loggingIn, setLoggingIn] = useState(false);

  const [stripeLink, setStripeLink] = useState('');
  const [buttonText, setButtonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/settings`);
      setStripeLink(data.stripe_link || '');
      setButtonText(data.button_text || '');
    } catch (e) {
      toast.error("Impossible de charger les paramètres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) loadSettings();
  }, [token, loadSettings]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      toast.success('Connexion réussie');
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Échec de la connexion');
    } finally {
      setLoggingIn(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/settings`,
        { stripe_link: stripeLink, button_text: buttonText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Modifications enregistrées ✅');
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error('Session expirée, reconnectez-vous.');
        logout();
      } else {
        toast.error("Erreur lors de l'enregistrement.");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------- Login screen ----------
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md border-[#01C38D]/20 shadow-lg" data-testid="admin-login-card">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-full bg-[#01C38D]/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-[#01C38D]" />
            </div>
            <CardTitle className="text-2xl">Espace administrateur</CardTitle>
            <CardDescription>Connectez-vous pour gérer votre site</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="admin-email-input"
                  placeholder="admin@swissborg.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Mot de passe</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="admin-password-input"
                  placeholder="••••••••"
                />
              </div>
              <Button
                type="submit"
                disabled={loggingIn}
                className="w-full bg-[#01C38D] hover:bg-[#00A878] text-white"
                data-testid="admin-login-button"
              >
                {loggingIn ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Se connecter'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Settings screen ----------
  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Paramètres du site</h1>
          <Button
            variant="outline"
            onClick={logout}
            className="gap-2"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </Button>
        </div>

        <Card className="border-[#01C38D]/20 shadow-sm">
          <CardHeader>
            <CardTitle>Bouton de paiement</CardTitle>
            <CardDescription>
              Modifiez le lien Stripe et le texte du bouton principal. Les changements sont appliqués
              immédiatement sur le site.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center gap-2 text-gray-500 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
              </div>
            ) : (
              <form onSubmit={handleSave} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="stripe">Lien de paiement Stripe</Label>
                  <Input
                    id="stripe"
                    type="url"
                    value={stripeLink}
                    onChange={(e) => setStripeLink(e.target.value)}
                    required
                    placeholder="https://buy.stripe.com/..."
                    data-testid="settings-stripe-input"
                  />
                  <p className="text-xs text-gray-500">L'adresse du lien de paiement Stripe à ouvrir.</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="btntext">Texte du bouton</Label>
                  <Textarea
                    id="btntext"
                    value={buttonText}
                    onChange={(e) => setButtonText(e.target.value)}
                    required
                    rows={2}
                    placeholder="Ex: Acheter maintenant"
                    data-testid="settings-buttontext-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#01C38D] hover:bg-[#00A878] text-white gap-2"
                  data-testid="settings-save-button"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Admin;
